import { FormEvent, useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { ApiEndpoints, reqGetNew, reqPatch, reqPostNew, reqPut } from "../../../../config/apiClient";
import styles from "../styles/ronda.module.css";

interface RondaPonto {
    id_qrcode: number | { id_qrcode: number };
}

interface QrCode {
    id_qrcode: number;
    descricao: string;
    sub_descricao?: string | null;
    id_centro_custo?: { codigo: number; centro_custo: string } | null;
}

interface Ronda {
    id_ronda: number | null;
    descricao: string;
    ronda_pontos: RondaPonto[] | number[];
    id_centro_custo: number | null;
    centro_custo: string | null;
    tempo_estimado: string | null;
    latitude: string | number | null;
    longitude: string | number | null;
    situacao: number;
}

interface RondaCadastroModalProps {
    show: boolean;
    idRonda: number | null;
    onClose: () => void;
    onSaved: () => void;
}

const emptyRonda: Ronda = {
    id_ronda: null,
    descricao: "",
    ronda_pontos: [],
    id_centro_custo: null,
    centro_custo: null,
    tempo_estimado: null,
    latitude: null,
    longitude: null,
    situacao: 1,
};

function getPontoId(ponto: RondaPonto | number): number {
    if (typeof ponto === "number") {
        return ponto;
    }
    return typeof ponto.id_qrcode === "number" ? ponto.id_qrcode : ponto.id_qrcode.id_qrcode;
}

function formatTempo(value: string): string {
    const digits = value.replace(/\D/g, "").replace(/^0+/, "").slice(0, 6);
    if (!digits) {
        return "";
    }
    const minutes = digits.slice(-2).padStart(2, "0");
    const hours = digits.slice(0, -2).padStart(2, "0");
    return `${hours}:${minutes}`;
}

function formatCoordenada(value: string): string {
    let sanitized = value.replace(",", ".").replace(/[^\d.-]/g, "");
    const negative = sanitized.startsWith("-");
    sanitized = sanitized.replace(/-/g, "");
    const parts = sanitized.split(".");
    const integerPart = parts[0].slice(0, 3);
    const decimalPart = parts.slice(1).join("").slice(0, 10);
    return `${negative ? "-" : ""}${integerPart}${parts.length > 1 ? `.${decimalPart}` : ""}`;
}

function coordenadaValida(value: string, maximo: number): boolean {
    if (value.trim() === "") {
        return true;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= -maximo && parsed <= maximo;
}

function coordenadasPreenchidas(latitude: string, longitude: string): boolean {
    const latitudePreenchida = latitude.trim() !== "";
    const longitudePreenchida = longitude.trim() !== "";
    return (!latitudePreenchida && !longitudePreenchida) || (latitudePreenchida && longitudePreenchida);
}

function coordenadaInvalida(value: string, outroValor: string, maximo: number): boolean {
    return (value.trim() === "" && outroValor.trim() !== "") || !coordenadaValida(value, maximo);
}

function tempoValido(value: string | null): boolean {
    return !value || (/^\d{1,4}:\d{2}$/.test(value) && Number(value.split(":")[1]) < 60);
}

export default function RondaCadastroModal({ show, idRonda, onClose, onSaved }: RondaCadastroModalProps) {
    const editing = idRonda !== null;
    const [ronda, setRonda] = useState<Ronda>(emptyRonda);
    const [pontos, setPontos] = useState<number[]>([]);
    const [qrcodes, setQrcodes] = useState<QrCode[]>([]);
    const [qrcodeSelecionado, setQrcodeSelecionado] = useState("");
    const [centrosCusto, setCentrosCusto] = useState<any[]>([]);
    const [centroCustoSelecionado, setCentroCustoSelecionado] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [descricaoInvalida, setDescricaoInvalida] = useState(false);
    const [formularioEnviado, setFormularioEnviado] = useState(false);

    useEffect(() => {
        if (!show) {
            return;
        }
        setRonda({ ...emptyRonda, id_ronda: idRonda });
        setPontos([]);
        setQrcodeSelecionado("");
        setCentroCustoSelecionado(null);
        setDescricaoInvalida(false);
        setFormularioEnviado(false);
        setLoading(true);
        async function carregarDados() {
            const [, centrosCustoCarregados] = await Promise.all([carregarQrcodes(), carregarCentrosCusto()]);
            if (idRonda !== null) {
                await carregarRonda(idRonda, centrosCustoCarregados);
            }
            setLoading(false);
        }
        void carregarDados();
        // A consulta acompanha a abertura e o registro selecionado do modal.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, idRonda]);

    async function carregarRonda(id: number, centrosCustoDisponiveis: any[]) {
        const response = await reqPostNew(ApiEndpoints.qrcodeRondaConsultar, { id_ronda: id });
        if (response.status !== 200 || !response.data) {
            toast.error("Não foi possível consultar a ronda.", { containerId: 1, position: "top-center", theme: "dark" });
            onClose();
            return;
        }
        const dados = response.data as Ronda;
        setRonda({
            ...emptyRonda,
            ...dados,
            id_ronda: id,
            tempo_estimado: dados.tempo_estimado === null ? null : String(dados.tempo_estimado),
        });
        setPontos((dados.ronda_pontos || []).map(getPontoId));
        if (dados.id_centro_custo !== null) {
            setCentroCustoSelecionado(
                centrosCustoDisponiveis.find((item) => Number(item.value) === Number(dados.id_centro_custo)) || {
                    value: String(dados.id_centro_custo),
                    label: `${String(dados.id_centro_custo)} - ${dados.centro_custo}`,
                },
            );
        }
    }

    async function carregarCentrosCusto(): Promise<any[]> {
        const response = await reqGetNew(ApiEndpoints.qrcodeFiltros);
        if (response.status !== 200 || !response.data?.centros_custo) {
            toast.error("Não foi possível carregar os Centros de Custo.", { containerId: 1, position: "top-center", theme: "dark" });
            return [];
        }
        const centrosCustoCarregados = [
            { value: 0, label: "Nenhum selecionado" },
            ...response.data.centros_custo.map((item: any) => ({
                value: String(item.codigo),
                label: `${item.codigo} - ${item.centro_custo}`,
            })),
        ];
        setCentrosCusto(centrosCustoCarregados);
        return centrosCustoCarregados;
    }

    async function carregarQrcodes() {
        const response = await reqPostNew(ApiEndpoints.qrcodeListar, {
            id_empresa: [],
            id_centro_custo: [],
            situacao: [0],
        });
        if (response.status !== 200 || !Array.isArray(response.data)) {
            toast.error("Não foi possível carregar os QR Codes.", { containerId: 1, position: "top-center", theme: "dark" });
            return;
        }
        setQrcodes(response.data as QrCode[]);
    }

    async function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setFormularioEnviado(true);
        const descricao = ronda.descricao.trim();
        setDescricaoInvalida(!descricao);
        const latitude = String(ronda.latitude ?? "");
        const longitude = String(ronda.longitude ?? "");
        if (!descricao || !tempoValido(ronda.tempo_estimado) || !coordenadasPreenchidas(latitude, longitude) || !coordenadaValida(latitude, 90) || !coordenadaValida(longitude, 180)) {
            return;
        }
        console.log(`ronda_dados`)
        console.log(ronda)
        const dados = {
            id_ronda: editing ? ronda.id_ronda : null,
            descricao,
            ronda_pontos: pontos,
            id_centro_custo: centroCustoSelecionado && Number(centroCustoSelecionado.value) !== 0 ? Number(centroCustoSelecionado.value) : null,
            tempo_estimado: ronda.tempo_estimado,
            latitude: ronda.latitude === "" ? null : ronda.latitude,
            longitude: ronda.longitude === "" ? null : ronda.longitude,
            situacao: ronda.situacao,
        };

        setLoading(true);
        const response = editing ? await reqPatch(ApiEndpoints.qrcodeRondaEditar, dados) : await reqPut(ApiEndpoints.qrcodeRondaCadastrar, dados);
        setLoading(false);
        if (response.status !== 200 && response.status !== 201) {
            // toast.error("Não foi possível salvar a ronda.", { containerId: 1, position: "top-center", theme: "dark" });
            return;
        }

        toast.success(editing ? "Ronda editada com sucesso!" : "Ronda cadastrada com sucesso!", {
            containerId: 1,
            position: "top-center",
            theme: "dark",
        });
        onClose();
        onSaved();
    }

    const updateRonda = <K extends keyof Ronda>(field: K, value: Ronda[K]) => {
        setRonda((current) => ({ ...current, [field]: value }));
    };

    function adicionarPonto() {
        const id = Number(qrcodeSelecionado);
        if (!id || pontos.includes(id)) {
            return;
        }
        setPontos((current) => [...current, id]);
        setQrcodeSelecionado("");
    }

    function moverPonto(index: number, direction: -1 | 1) {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= pontos.length) {
            return;
        }
        setPontos((current) => {
            const reordered = [...current];
            [reordered[index], reordered[newIndex]] = [reordered[newIndex], reordered[index]];
            return reordered;
        });
    }

    return (
        <Modal id={styles.modal_qrcode_ronda} show={show} onHide={onClose} backdrop="static" keyboard={true} size="lg">
            <Form onSubmit={handleSubmit} noValidate>
                <Modal.Header closeButton>
                    <Modal.Title><b>{editing ? "Editar Ronda" : "Cadastrar Ronda"}</b></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loading ? <Spinner animation="border" /> : null}
                    <Row className="mb-3">
                        <Col md={8}>
                            <Form.Group controlId="ronda-descricao">
                                <Form.Label>Descrição:</Form.Label>
                                <Form.Control
                                    value={ronda.descricao}
                                    onChange={(event) => {
                                        updateRonda("descricao", event.target.value);
                                        setDescricaoInvalida(false);
                                    }}
                                    isInvalid={descricaoInvalida}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">Informe a descrição da ronda.</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group controlId="ronda-situacao" className={styles.status_group}>
                                <Form.Label>Situação:</Form.Label>
                                <div className={`${styles.status_control} ${ronda.situacao === 1 ? styles.status_active : styles.status_inactive}`}>
                                    <Form.Check type="switch" id="ronda-situacao-switch" checked={ronda.situacao === 1} onChange={(event) => updateRonda("situacao", event.target.checked ? 1 : 0)} label={ronda.situacao === 1 ? "Ronda ativa" : "Ronda inativa"} />
                                    <span className={styles.status_badge}>{ronda.situacao === 1 ? "Ativa" : "Inativa"}</span>
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={8}>
                            <Form.Group controlId="ronda-centro-custo">
                                <Form.Label>Centro de Custo:</Form.Label>
                                <Select name="centro_custo" options={centrosCusto} value={centroCustoSelecionado} placeholder="Selecione o Centro de Custo" isClearable onChange={(choice: any) => setCentroCustoSelecionado(choice)} isDisabled={loading} styles={{ control: (baseStyles) => ({ ...baseStyles, borderColor: "#dee2e6" }) }} />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group controlId="ronda-latitude">
                                <Form.Label>Latitude:</Form.Label>
                                <Form.Control
                                    value={ronda.latitude ?? ""}
                                    onChange={(event) => updateRonda("latitude", formatCoordenada(event.target.value))}
                                    placeholder=""
                                    isInvalid={formularioEnviado && coordenadaInvalida(String(ronda.latitude ?? ""), String(ronda.longitude ?? ""), 90)}
                                />
                                <Form.Control.Feedback type="invalid">Informe a latitude.</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group controlId="ronda-longitude">
                                <Form.Label>Longitude:</Form.Label>
                                <Form.Control
                                    value={ronda.longitude ?? ""}
                                    onChange={(event) => updateRonda("longitude", formatCoordenada(event.target.value))}
                                    placeholder=""
                                    isInvalid={formularioEnviado && coordenadaInvalida(String(ronda.longitude ?? ""), String(ronda.latitude ?? ""), 180)}
                                />
                                <Form.Control.Feedback type="invalid">Informe a longitude.</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={9}>
                            <Form.Group controlId="ronda-pontos">
                                <Form.Label>Pontos de Verificação:</Form.Label>
                                <Row className="g-2 mb-2">
                                    <Col>
                                        <Form.Select value={qrcodeSelecionado} onChange={(event) => setQrcodeSelecionado(event.target.value)} disabled={loading}>
                                            <option value="">Selecione um QR Code</option>
                                            {qrcodes
                                                .filter((qrcode) => !pontos.includes(qrcode.id_qrcode))
                                                .map((qrcode) => (
                                                    <option key={qrcode.id_qrcode} value={qrcode.id_qrcode}>
                                                        #{qrcode.id_qrcode} - {qrcode.descricao}
                                                    </option>
                                                ))}
                                        </Form.Select>
                                    </Col>
                                    <Col xs="auto">
                                        <Button type="button" onClick={adicionarPonto} disabled={!qrcodeSelecionado || loading}>
                                            Adicionar
                                        </Button>
                                    </Col>
                                </Row>
                                {pontos.length === 0 ? (
                                    <Form.Text>Nenhum QR Code adicionado.</Form.Text>
                                ) : (
                                    <div className="border rounded p-2">
                                        {pontos.map((id, index) => {
                                            const qrcode = qrcodes.find((item) => item.id_qrcode === id);
                                            return (
                                                <div key={`${id}-${index}`} className="d-flex align-items-center gap-2 border-bottom py-2">
                                                    <strong>{index + 1}.</strong>
                                                    <span className="flex-grow-1">
                                                        #{id} - {qrcode?.descricao || "QR Code não encontrado"}
                                                    </span>
                                                    <Button type="button" size="sm" variant="outline-secondary" onClick={() => moverPonto(index, -1)} disabled={index === 0}>
                                                        ↑
                                                    </Button>
                                                    <Button type="button" size="sm" variant="outline-secondary" onClick={() => moverPonto(index, 1)} disabled={index === pontos.length - 1}>
                                                        ↓
                                                    </Button>
                                                    <Button type="button" size="sm" variant="outline-danger" onClick={() => setPontos((current) => current.filter((_, itemIndex) => itemIndex !== index))}>
                                                        Remover
                                                    </Button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group controlId="ronda-tempo">
                                <Form.Label>Tempo estimado:</Form.Label>
                                <Form.Control
                                    value={ronda.tempo_estimado ?? ""}
                                    onChange={(event) => updateRonda("tempo_estimado", formatTempo(event.target.value))}
                                    placeholder="HH:mm"
                                    inputMode="numeric"
                                    maxLength={7}
                                    isInvalid={!tempoValido(ronda.tempo_estimado)}
                                />
                                {/*<Form.Text>Até 4 dígitos para horas e 2 para minutos.</Form.Text>*/}
                                <Form.Control.Feedback type="invalid">Use HH:mm e informe minutos entre 00 e 59.</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button variant="success" type="submit" disabled={loading}>
                        {loading ? "Aguarde ..." : editing ? "Editar" : "Cadastrar"}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
